import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import authService from '../services/auth/auth-service.js';

const useAuthStore = create(
  persist(
    (set, get) => ({
      // ==================== STATE ====================
      user: authService.getCurrentUser(),
      token: authService.getToken(),
      permissions: authService.getPermissions(),
      isAuthenticated: !!authService.getToken(),

      // Client context (Superadmin only)
      availableClients: [],
      selectedClientId: null, // null = own client, number = act as client

      // UI state
      isLoading: false,
      isLoggedOut: false,
      error: null,
      redirectTo: null,
      redirectReason: null,

      // Settings State
      appSettings: {}, 
      settingsErrors: {},
      landingPage: null, // Stores landing page from 'auth' settings request
      

      // ==================== ACTIONS ====================
      setAuth: (token, user, permissions = []) => {
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('permissions', JSON.stringify(permissions));

        set({
          token,
          user,
          permissions,
          isAuthenticated: true,
          isLoggedOut: false,
          error: null,
        });
      },

      clearAuth: () => {
        authService.clearAuthData();
        set({
          token: null,
          user: null,
          permissions: [],
          isAuthenticated: false,
          isLoggedOut: true,
          error: null,
          redirectTo: null,
          redirectReason: null,
          selectedClientId: null,
          availableClients: [],
          appSettings: {}, // Clear settings on logout
          settingsErrors: {},
          landingPage: null, // Clear landing page
        });
      },

      updateUser: (userData) => {
        const updatedUser = { ...get().user, ...userData };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        set({ user: updatedUser });
      },

      setLoading: (isLoading) => {
        set({ isLoading });
      },

      setError: (error) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      syncAuthState: () => {
        const token = authService.getToken();
        const user = authService.getCurrentUser();
        const permissions = authService.getPermissions();

        set({
          token,
          user,
          permissions,
          isAuthenticated: !!token,
        });

        return !!token;
      },

      // ==================== REDIRECT MANAGEMENT ====================
      setRedirect: (path, state, reason = 'unauthorized') => {        
        set({
          redirectTo: { path, state },
          redirectReason: reason,
        });
      },

      clearRedirect: () => {
        set({
          redirectTo: null,
          redirectReason: null,
        });
      },

      // ==================== PERMISSION HELPERS ====================
      hasViewPermission: (module) => {
        const { permissions } = get();
        return permissions.includes(`view_${module}`);
      },

      hasAnyViewPermission: (modules) => {
        const { permissions } = get();
        return modules.some((module) => permissions.includes(`view_${module}`));
      },

      // ==================== CLIENT CONTEXT (SUPERADMIN) ====================
      isSuperadmin: () => {
        const { user } = get();
        return user?.role_name === 'Super Admin';
      },

      setAvailableClients: (clients) => {
        set({ availableClients: clients });
      },

      setClientContext: (clientId) => {
        const { isSuperadmin } = get();
        if (!isSuperadmin()) {
          console.warn('⚠️ Only Superadmin can switch client contexts');
          return false;
        }
        console.log(`🔄 Switching client context to: ${clientId}`);
        set({ selectedClientId: clientId });
        return true;
      },

      clearClientContext: () => {
        console.log('🔄 Clearing client context');
        set({ selectedClientId: null });
      },

      getSelectedClient: () => {
        const { availableClients, selectedClientId } = get();
        if (!selectedClientId) return null;
        return availableClients.find(client => client.id === selectedClientId) || null;
      },

      getOwnClientId: () => {
        const { user } = get();
        return user?.client_id || null;
      },

      getEffectiveClientId: () => {
        const { selectedClientId, getOwnClientId } = get();
        return selectedClientId || getOwnClientId();
      },

      isActingAsOtherClient: () => {
        const { selectedClientId } = get();
        return selectedClientId !== null;
      },

      // ==================== SETTINGS ACTIONS ====================
      setAppSettings: (appName, settings) => {
        set((state) => ({
          appSettings: {
            ...state.appSettings,
            [appName]: settings,
          },
          settingsErrors: {
            ...state.settingsErrors,
            [appName]: null, // Clear error on success
          },
        }));
      },

      setSettingsError: (appName, error) => {
        set((state) => ({
          settingsErrors: {
            ...state.settingsErrors,
            [appName]: error,
          },
        }));
      },

      setLandingPage: (path) => {
        console.log('✅ Landing page set:', path);
        set({ landingPage: path });
      },

      getAppSettings: (appName) => {
        return get().appSettings[appName] || null;
      },

      getSettingCategory: (appName, category) => {
        const settings = get().appSettings[appName];
        return settings?.[category] || null;
      },

      getLandingPage: () => {
        const { redirectTo, redirectReason, landingPage } = get();

        // Priority 1: Unauthorized redirect
        if (redirectTo?.path && redirectReason === 'unauthorized') {
          return redirectTo.path;
        }

        // Priority 2: Landing page from auth settings (set by useAppSettings hook)
        return landingPage;
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        permissions: state.permissions,
        isAuthenticated: state.isAuthenticated,
        selectedClientId: state.selectedClientId,
        appSettings: state.appSettings,
        landingPage: state.landingPage, // Persist landing page
      }),
    }
  )
);

export default useAuthStore;